import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Plane, ArrowRight, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "client" ? "/portal" : "/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grain-overlay flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-slate-800/80 shadow-2xl shadow-black/60">
        {/* Left brand panel */}
        <div className="hidden lg:flex flex-col justify-between bg-[#0D0E15] p-10 relative">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Plane className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-extrabold text-2xl text-slate-100 tracking-tight">ApplyPilot</span>
          </div>
          <div className="space-y-5">
            <h1 className="font-heading text-4xl font-bold text-slate-100 leading-tight tracking-tight">
              Evaluate any job in <span className="text-indigo-400">seconds</span>, not minutes.
            </h1>
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              Set up a client once. Browse job boards normally and instantly see APPLY or DO NOT APPLY
              — with reasons — right on the page. Every decision logged automatically.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {["Consistent decisions", "One-click client switch", "Zero copy-paste", "Auto tracker"].map((t) => (
                <span key={t} className="text-xs text-slate-300 bg-slate-800/60 border border-slate-700/60 px-3 py-1 rounded-full">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-slate-600 font-mono">Human always clicks apply · Never auto-submits</div>
        </div>

        {/* Right form */}
        <div className="bg-[#12151F] p-8 sm:p-10 flex flex-col justify-center">
          <h2 className="font-heading text-2xl font-bold text-slate-100">Welcome back</h2>
          <p className="text-slate-400 text-sm mt-1 mb-6">Sign in to your executive workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3.5 py-2.5 text-sm text-slate-100 outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3.5 py-2.5 text-sm text-slate-100 outline-none focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-lg py-2.5 text-sm transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
